import { reset } from "../slices/shared";
import { reconcileImageCache } from "../slices/api";
import { postLiveCourse, putFeedback, uploadImage } from "../thunks/api";

export const syncMiddleware = (store) => {
  let timeout = null;
  let stopped = false;
  // Keys currently being uploaded. Without this a slow upload gets
  // re-dispatched on every 10s cycle, which is why a single photo could end
  // up hitting /media eight times in thirty seconds.
  const inFlight = new Set();

  schedule(0);

  return (next) => (action) => {
    next(action);

    if (action.type === reset().type) {
      clearTimeout(timeout);
      timeout = null;
      stopped = true;
    } else if (stopped) {
      // Restart after a reset. This used to be `if (!timeout)`, but timeout
      // still held the cleared handle, so syncing stayed dead until reload.
      stopped = false;
      schedule(0);
    }
  };

  function schedule(delay) {
    clearTimeout(timeout);
    timeout = setTimeout(sync, delay);
  }

  async function sync() {
    try {
      const state = store.getState();
      saveFeedbacks(store);
      if (state.user.type !== "TRAINER") return;
      saveCourse(store);
      await uploadImages(store, inFlight);
    } catch (error) {
      // One bad cycle must never kill the loop. Before this, a throw here
      // left the trainer on "Synchronising..." indefinitely - only a page
      // reload would start syncing again.
      console.error("Sync cycle failed", error);
    } finally {
      if (!stopped) schedule(10000);
    }
  }
};

function saveFeedbacks(store) {
  const queuedFeedback = store.getState()?.api.queuedFeedback || [];
  if (!queuedFeedback.length) return;

  queuedFeedback.forEach((item) => {
    return store.dispatch(putFeedback(item));
  });
}

function saveCourse(store) {
  const state = store.getState();
  const toUpload = getUploadData(state);
  if (toUpload && !state.api.savedCourses.includes(toUpload.courseId)) {
    return store.dispatch(postLiveCourse(toUpload));
  }
}

async function uploadImages(store, inFlight) {
  const cache = await caches.open("imageCache");
  const items = await cache.keys();
  const present = items.map((item) => item.url.split("/")[3]).filter(Boolean);

  // Drop cached-image bookkeeping for keys that are no longer in Cache
  // Storage and were never recorded as uploaded - Safari evicts Cache
  // Storage, and a tab killed between the upload and the persist write
  // loses the "uploaded" record. Either way the key is unrecoverable, and
  // keeping it pins "Synchronising..." on for every later course too.
  store.dispatch(reconcileImageCache(present));

  const { uploadedImages } = store.getState().api;

  present.forEach((key) => {
    if (uploadedImages.includes(key) || inFlight.has(key)) return;
    inFlight.add(key);
    store.dispatch(uploadImage(key)).finally(() => inFlight.delete(key));
  });
}

function getUploadData(state) {
  const data = state.trainer.data;
  const stages = state.trainer.stages;
  const course = state.trainer.course;
  const delegates = state.trainer.delegates;

  if (!data || !stages || !course || !delegates) return null;

  return {
    courseId: course._id,
    data: data.map((r, i) => {
      return {
        data: r,
        stageType: stages[i]?.Type,
      };
    }),
  };
}
